<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { BaseAvatar, BaseButton } from '@magma-app/magma-lapilli';

const route = useRoute();
const root = ref<HTMLElement>();
const english = computed(() => route.query.lang === 'en');
const copy = computed(() => english.value ? {
  title: 'This ambassador program has ended.',
  description: 'New connections are always possible. '
    + 'Magma helps organizations bring their communities together through their ambassadors.',
  action: 'Discover Magma', label: 'The ambassador platform',
} : {
  title: 'Ce programme ambassadeur est arrêté.',
  description: 'Les rencontres continuent avec Magma. '
    + 'Nous aidons les organisations à faire vivre leur communauté grâce à leurs ambassadeurs.',
  action: 'Découvrir Magma', label: 'La plateforme des ambassadeurs',
});

const discoverMagma = () => {
  window.open('https://magma.app?utm_source=closed_program&utm_medium=embed', '_blank', 'noopener,noreferrer');
};

let observer: ResizeObserver | undefined;
const notifyParent = () => {
  if (window.parent === window || !document.referrer) return;
  try {
    // The parent may resize the iframe; the promotional screen stays visible by default.
    window.parent.postMessage({ type: 'magma:program-ended', height: root.value?.scrollHeight || 560 },
      new URL(document.referrer).origin);
  } catch { /* A missing parent origin requires no reporting. */ }
};
onMounted(() => {
  notifyParent();
  observer = new ResizeObserver(notifyParent);
  if (root.value) observer.observe(root.value);
});
onUnmounted(() => observer?.disconnect());
</script>

<template>
  <main
    ref="root"
    class="program-ended grid min-h-dvh place-items-center bg-background-brand-default
      px-4 py-6 text-center text-foreground-emphasis sm:p-8"
    :lang="english ? 'en' : 'fr'"
  >
    <div class="w-full max-w-xl rounded-xl border border-border-subtle bg-background-default p-6 shadow-sm sm:p-12">
      <a
        class="inline-flex items-center gap-3 rounded-lg focus-visible:outline focus-visible:outline-2
          focus-visible:outline-offset-4 focus-visible:outline-border-brand-emphasis"
        href="https://magma.app"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Magma"
      >
        <BaseAvatar src="/img/logo_magma.png" alt="" size="md" shape="rounded" />
        <span class="text-mgm-dspl-md font-semibold tracking-tight text-foreground-brand-default">magma</span>
      </a>
      <p class="mt-2 text-mgm-txt-sm font-medium text-foreground-brand-default">
        {{ copy.label }}
      </p>
      <div class="mx-auto mb-6 mt-6 max-w-md sm:mb-8 sm:mt-8">
        <h1 class="text-balance text-mgm-dspl-xs font-semibold tracking-tight sm:text-mgm-dspl-sm">{{ copy.title }}</h1>
        <p class="mt-4 text-mgm-txt-sm text-foreground-default sm:text-mgm-txt-md">{{ copy.description }}</p>
      </div>
      <BaseButton
        class="mx-auto w-full sm:w-auto"
        :text="copy.action"
        size="xl"
        state="primary"
        type="button"
        icon="arrow-up-right"
        icon-position="right"
        @click="discoverMagma"
      />
      <p class="mt-4 text-mgm-txt-sm text-foreground-default">magma.app</p>
    </div>
  </main>
</template>

<style scoped>
.program-ended {
  padding-top: max(1.5rem, env(safe-area-inset-top));
  padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
}
</style>
